import * as gsf from "core/src/core/game-state-functions.js";
import { ARENA_SIZES, arenaOf, arenaValue, nearestSizeKey, normalizeArenaValue } from "core/src/core/arena-sizes.js";
import type { GameMap } from "../../shared/types";

// Arenas are picked as a SHAPE and a SIZE (Create lobby, Play locally). A size is
// an area, the same for every shape (core arena-sizes.js): Small 520 (a 1v1),
// Medium 840, Large 1170, Huge 1590 (a computer's screen). The standard shapes are
// the landscape 2:1 rectangle and the 2:1 stadium (a pill lying along x); Square
// and Circle stay selectable. A map travels as its value string ("Rectangle 735
// 368", "Stadium 778 389", "Square 520", "Circle 587"), parsed by core's
// createMapFromValue.
export type MapShape = "rectangle" | "stadium" | "square" | "circle";
export type MapSizeKey = "small" | "medium" | "large" | "huge";

// A shape's player-facing name.
export const SHAPE_NAMES: Record<MapShape, string> = { rectangle: "Rectangle", stadium: "Stadium", square: "Square", circle: "Circle" };
export const MAP_SHAPES: MapShape[] = ["rectangle", "stadium", "square", "circle"];

export interface MapSize {
  key: MapSizeKey;
  label: string;
  side: number; // the side of the square with this area
}
export const MAP_SIZES: MapSize[] = ARENA_SIZES as MapSize[];

export type MapDesc =
  | { type: "Square"; size: number }
  | { type: "Circle"; radius: number }
  | { type: "Rectangle"; width: number; height: number }
  | { type: "Stadium"; width: number; height: number };

// The map value for a shape at a size ("Rectangle 735 368").
export function mapValueFor(shape: MapShape, size: MapSizeKey): string {
  return arenaValue(shape, size);
}

// Local play and custom lobbies start on a Small Rectangle.
export const DEFAULT_MAP_VALUE = mapValueFor("rectangle", "small"); // "Rectangle 735 368"

// A map value's shape and nearest standard size (null for a value it can't read).
export function shapeAndSizeOf(mapString: string): { shape: MapShape; size: MapSizeKey } | null {
  const a = arenaOf(mapString);
  return a ? { shape: a.shape as MapShape, size: nearestSizeKey(a.side) as MapSizeKey } : null;
}

// Any map value snapped to its shape at the nearest standard size: an old saved
// "Square 500" becomes "Square 520", a "Rectangle 990 495" the Medium Rectangle.
export function normalizeMapValue(mapString: string): string {
  return normalizeArenaValue(mapString);
}

// What a map string looks like, for thumbnails and labels: its shape, its size
// (the side of the square with its area, whatever the shape) and width / height.
// null for a string it can't read.
export function arenaShapeOf(mapString: string): { shape: MapShape; size: number; aspect: number } | null {
  const a = arenaOf(mapString);
  if (!a) return null;
  const [, w, h] = String(mapString).split(" ").map(Number);
  const aspect = a.shape === "rectangle" || a.shape === "stadium" ? w / h : 1;
  return { shape: a.shape as MapShape, size: a.side, aspect };
}

// How much of a thumbnail's box a map of this size fills: Small reads small,
// Huge fills it.
export function glyphFill(size: number): number {
  const lo = MAP_SIZES[0].side, hi = MAP_SIZES[MAP_SIZES.length - 1].side;
  const t = Math.max(0, Math.min(1, Math.log(size / lo) / Math.log(hi / lo)));
  return 0.55 + 0.43 * t;
}

// A thumbnail's box inside a 100 x 100 viewBox: the longest side is `long`
// (callers scale it by the map's size), the other follows the map's aspect.
export function glyphBox(aspect: number, long: number): { x: number; y: number; w: number; h: number } {
  const w = aspect >= 1 ? long : long * aspect;
  const h = aspect >= 1 ? long / aspect : long;
  return { x: (100 - w) / 2, y: (100 - h) / 2, w, h };
}

// The small map descriptor the edge-server expects; mirrors createMapFromValue
// (Circle radius = size / 2). Sent to the server for a lobby.
export function mapDescriptor(mapString: string): MapDesc {
  const [type, a, b] = mapString.split(" ");
  const n = Number(a);
  if (type === "Circle") return { type: "Circle", radius: n / 2 };
  if (type === "Rectangle") return { type: "Rectangle", width: n, height: Number(b) };
  if (type === "Stadium") return { type: "Stadium", width: n, height: Number(b) };
  if (type === "Square") return { type: "Square", size: n };
  return mapDescriptor(DEFAULT_MAP_VALUE);
}

// Human label for ANY arena string: its nearest standard size and its shape
// ("Medium Stadium"). Falls through to the raw string for shapes it doesn't know.
export function arenaLabel(mapString: string): string {
  const a = shapeAndSizeOf(mapString);
  if (!a) return mapString;
  const size = MAP_SIZES.find((s) => s.key === a.size)!;
  return `${size.label} ${SHAPE_NAMES[a.shape]}`;
}
export const mapLabelOf = arenaLabel;

// The inverse of mapDescriptor: a server map descriptor -> a map value string, so
// a guest can mirror the host's chosen map.
export function mapValueOf(desc: any): string {
  if (desc && desc.type === "Circle") return `Circle ${desc.radius * 2}`;
  if (desc && desc.type === "Square") return `Square ${desc.size}`;
  if (desc && desc.type === "Rectangle") return `Rectangle ${desc.width} ${desc.height}`;
  if (desc && desc.type === "Stadium") return `Stadium ${desc.width} ${desc.height}`;
  return DEFAULT_MAP_VALUE;
}

// Turn a map string into a core GameMap (core's createMapFromValue: "Square <side>",
// "Circle <diameter>", "Rectangle <width> <height>", "Stadium <width> <height>").
export function createMapFromValue(mapString: string): GameMap {
  return gsf.createMapFromValue(mapString);
}
